import { Component } from '@angular/core';
import { IonicPage, NavController, NavParams, LoadingController, AlertController, ModalController, Platform } from 'ionic-angular';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { BriefcasePage } from '../briefcase/briefcase';
import { NotificationsPage } from '../notifications/notifications';
import { WebserviceProvider } from '../../providers/webservice/webservice';
import { AuthProvider } from '../../providers/auth/auth';
import { StorageProvider } from '../../providers/storage/storage';
import { SynchroProvider } from '../../providers/synchro/synchro';
import { HelperProvider } from '../../providers/helper/helper';

import { SynchroProgress } from '../../interfaces/synchro-progress.model';
import { Notification } from '../../interfaces/notification.model';
import { NotificationProvider } from '../../providers/notification/notification';
import { UserInfo } from '../../interfaces/user-info.model';
import { UserInformationProvider } from '../../providers/user-information/user-information';
import { Insomnia } from '@ionic-native/insomnia';

import * as localForage from "localforage";



@IonicPage()
@Component({
  selector: 'page-login',
  templateUrl: 'login.html',
})
export class LoginPage {

  ping: any;
  email: string;
  languages: Array<{title: string, value: string}>;
  selectedLanguage: string;
  user: any;
  loginForm: FormGroup;
  loading: any;
  progress: SynchroProgress;
  startSyncDate: string;
  lastSyncDate: string;
  lastOnlineLoginDate: string;
  synchroList: any;
  notifications: Notification[];
  userInformation: UserInfo;
  isIosPlatform: boolean;

  constructor(
    public plt: Platform,
    public navCtrl: NavController, 
    public navParams: NavParams, 
    public translate: TranslateService, 
    public authService: AuthProvider,
    public webservice: WebserviceProvider,
    public loadingCtrl: LoadingController, 
    public alertCtrl: AlertController,
    public storageService: StorageProvider,
    public synchroService: SynchroProvider,
    public helperService: HelperProvider,
    public notificationService: NotificationProvider,
    public userInfoService: UserInformationProvider,
    public modalCtrl: ModalController,
    private fb: FormBuilder,
    private insomnia: Insomnia
  ) 
  {
    // this.isIosPlatform = this.plt.is('ios');
    //[VP - 27/01/2021] Use custom function to detect iOS because of ionic bug: https://github.com/ionic-team/ionic-v3/issues/1104
    this.isIosPlatform = this.helperService.isIOS();
    this.languages = [
      { title: 'DE', value: 'de' },
      { title: 'FR', value: 'fr' },
      { title: 'IT', value: 'it' }
    ];
    if(this.isIosPlatform){
      this.loginForm = this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        selectedLanguage: ['de']
      });
    } else {
      this.loginForm = this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required]],
        selectedLanguage: ['de']
      });
    }
    this.loading = null; 
  }

  ionViewDidLoad() {
    if(this.isIosPlatform){
      this.autoLogin();
    } else {
      this.selectLanguage();
    }
  }

  selectLanguage(){
    const selectedLanguage = this.loginForm.get("selectedLanguage").value;
    if(selectedLanguage !== null){
      this.storageService.setDataStorage("selectedLanguage", selectedLanguage.toUpperCase());
    }
    this.translate.use(selectedLanguage);
  }

  autoLogin(){
    this.storageService.getUser().then(user => {
      this.user = user;
      if(this.user !== null){
        this.loginForm.setValue({
          email: this.user.Email,
          selectedLanguage: this.user.LanguageLanguageCode.toLowerCase()
        });
        this.selectLanguage();
        this.submitLogin();
      } else {
        this.selectLanguage();

        //No user found in storage: check if storage migration is needed
        if (this.storageService.storage.driver == 'cordovaSQLiteDriver') {
          //Check if existing data in asyncStorage
          var store = localForage.createInstance({
            name: '_ionicstorage',
            storeName: '_ionickv'
          });
          store.setDriver(localForage.INDEXEDDB).then(() => {
            console.log("***Store driver: ",store.driver());

            //Can I get data from that store???
            store.getItem("user").then((value: any) => {
              if (value && value.Email) {
                console.log("***User in INDEXEDDB store: ",value.Email);
                this.migrateStore(store);
              }
              else {
                console.log("***Invalid user value in INDEXEDDB store: ",value);
              }
            }).catch((reason) => {
              console.log("***Could not get user from INDEXEDDB store. Reason: ",reason);
            });
          })
          .catch((reason) => {
            console.log("***Store driver REJECTED: ",reason);
          });          
        }
      }
    });
  }

  async migrateStore(store: LocalForage) {
    console.log("***Proceed to data migration...");
    this.showLoading("Data migration...");

    store.iterate((value: any, key: string, iterationNumber: number) => {
      console.log('***Migration: step' + iterationNumber + ', key: ' + key + ', value: ', value);

      //Store same value in new Storage driver
      this.storageService.storage.set(key,value).then((result) => {
        console.log('***Migration success: ', result);
      }).catch((reason) => {
        console.log('***Migration error: ', reason);
      });
    }).then((result) => {
      this.showLoading("Data migration FINISHED!");
      console.log('***Migration FINISHED: ', result);
      this.dismissLoading();

      this.storageService.getUser().then(user => {
        this.user = user;
        if(this.user !== null){
          this.loginForm.setValue({
            email: this.user.Email,
            selectedLanguage: this.user.LanguageLanguageCode.toLowerCase()
          });
          this.selectLanguage();
          this.submitLogin();
        }
      });
    }).catch((reason) => {
      console.log('***Migration FAILED!!! Reason: ', reason);
      this.dismissLoading();
    });
  }

  isInvalidControl(name: string) {
    return this.loginForm.get(name).invalid && this.loginForm.get(name).dirty;
  }

  getPing(){
    return this.authService.ping();
  }

  successPing(){
    let promise1 = this.getLastLoginOnlineDate();
    let promise2 = this.synchroService.getSynchroDate();
    Promise.all([promise1, promise2]).then((values: Array<string>) => { 
      this.lastOnlineLoginDate = values[0];
      this.lastSyncDate = values[1];
      this.login();
    });
  }

  errorPing(){
    this.synchroService.getSynchroDate().then((data:string) => {
      this.lastSyncDate = data;
      this.getDataOffline();
    });
  }

  login(){
    if(this.isIosPlatform){
      this.authService.login(this.loginForm.value).subscribe(user => {
        this.user = user;
        this.storageService.setDataStorage("user", this.user);
        this.getDataOnline();
      }, error => {
        //console.log("error", error.error.Message);
        this.dismissLoading();
        this.showErrorLoginPopup();
      });
    } else {
      this.authService.loginWeb(this.loginForm.value).subscribe(user => {
        this.user = user;
        this.storageService.setDataStorage("user", this.user);
        this.getDataOnline();
      }, error => {
        this.dismissLoading();
        this.showErrorLoginPopup();
      });
    }

  }

  submitLogin(){
    this.showLoading(null);
    this.setLoginDate();
    this.getPing().subscribe(response => {
      this.ping = response;
      if(response){
        this.successPing();
      } else {
        this.errorPing();
      }
    }, error => {
      this.ping = error;
      this.errorPing();
    });
  }

  forgotPassword(){
    this.translate.get(['FORGOT_PASSWORD.TITLE','FORGOT_PASSWORD.MESSAGE',
          'FORGOT_PASSWORD.EMAIL', 'GENERAL.CANCEL', 'FORGOT_PASSWORD.RESET_PASSWORD']).subscribe(text => {
      const p = this.alertCtrl.create({
        title: text['FORGOT_PASSWORD.TITLE'],
        message: text['FORGOT_PASSWORD.MESSAGE'],
        inputs: [
          {
            name: 'email',
            placeholder: text['FORGOT_PASSWORD.EMAIL']
          }
        ],
        buttons: [
          {
            text: text['GENERAL.CANCEL'],
            handler: data => {
              console.log('Cancel clicked');
            }
          },
          {
            text: text['FORGOT_PASSWORD.RESET_PASSWORD'],
            handler: data => {
              if(data.email){
                this.sendResetPasswordEmail(data.email);
              } else {
                return false;
              }
            }
          }
        ]
      });
      p.present();
    });
  }

  sendResetPasswordEmail(email) {
    console.log('sendResetPasswordEmail: ', email);
    this.translate.get(['FORGOT_PASSWORD.CONFIRMATION.TITLE','FORGOT_PASSWORD.CONFIRMATION.MESSAGE', 'GENERAL.OK',
        'ERROR.ERROR', 'ERROR.PASSWORD_RESET_IMPOSSIBLE', 'ERROR.PASSWORD_RESET_FAILED']).subscribe(text => {
      this.authService.sendResetPassword(email).subscribe(result => {
        console.log('result: '+result);
        if (result) {
          //Show confirmation message
          const p = this.alertCtrl.create({
            title: text['FORGOT_PASSWORD.CONFIRMATION.TITLE'],
            message: text['FORGOT_PASSWORD.CONFIRMATION.MESSAGE'],
            buttons: [text['GENERAL.OK']]
          });
          p.present();
        }
        else {
          //Show error message
          const p = this.alertCtrl.create({
            title: text['ERROR.ERROR'],
            message: text['ERROR.PASSWORD_RESET_IMPOSSIBLE'],
            buttons: [text['GENERAL.OK']]
          });
          p.present();
        }
      }, error => {
        console.error("forgotPassword errror: ",error);
        //Show error message
        const p = this.alertCtrl.create({
          title: text['ERROR.PASSWORD_RESET_FAILED'],
          message: error.error.Message || error.statusText,
          buttons: [text['GENERAL.OK']]
        });
        p.present();
      });
    });
  }

  setLoginDate() {
    this.storageService.setDataStorage("loginDate", this.helperService.getFullDate(2));
  }

  getDataOnline(){
    this.startSyncDate = this.helperService.getFullDate(2);
    this.getAllData();
  }

  getDataOffline(){
    if(this.lastSyncDate && this.lastSyncDate !== null){
      this.storageService.getAllDataFromStorage()
      .then(data => {
        this.dismissLoading();
        this.navCtrl.setRoot(BriefcasePage);
      })
    } else {
      this.showRequireNetworkPopup().then(() => {
        this.dismissLoading();
      });
    }
  }

  //Get all data from webservice (first connection) or from storage
  getAllData() {
    if(this.isIosPlatform) {
      if(this.lastSyncDate && this.lastSyncDate !== null){
        this.getDataFromStorage();
      } else {
        this.getAllDataFromWS();
      }
    } else {
      this.getAllDataFromWSInWebApp();
    }
  }

  //Get all data from storage
  getDataFromStorage(){
    this.webservice.getNotifications(this.lastOnlineLoginDate).subscribe(notifWS => {
        this.notificationService.updateNotifsInStorage(notifWS)
        .then(res => {
          this.storageService.getAllDataFromStorage()
          .then(data => {
            this.dismissLoading();
            if(this.isIosPlatform) {
              //afficher les notifs
              this.notificationService.getNotifications()
                .then((notifications:Notification[]) => {
                  this.notifications = notifications;
                  this.notificationService.showAllNotifications()
                  .then(notifications => {
                    this.showLoading(null);
                    this.setLastLoginOnlineDate();
                    this.getSynchroList();
                  });
                });
            } else {
              this.showLoading(null);
              this.getSynchroList();
            }
          }).catch(error => {
            //TODO: HOW TO HANDLE ERRORS???
            console.log("Error getAllDataFromStorage: "+ error);
          });
        });
    });
  }

  getSynchroList(){
    this.synchroService.getSynchro(this.lastSyncDate).subscribe(synchroList => {
      this.setUserActions();
      this.synchroList = synchroList;
      if(this.synchroList.ResetUser){
        if(this.isIosPlatform){
          this.getAllDataFromWS();
        } else {
          this.getAllDataFromWSInWebApp();
        }
      } else {
        let found = this.synchroService.checkSynchroListsNotEmpty(this.synchroList);
        if(found){
          this.synchroProcess();
        } else {
          this.goToBriefcase();
        }
      }
    });
  }

  //Get all data from webservices into the iPad application
  getAllDataFromWS(){
    this.insomnia.keepAwake();
    
    this.webservice.getAllData().subscribe((progressData: SynchroProgress) => {
      this.translate.get(['SYNCHRO.LOADING']).subscribe(text => {
        this.progress = progressData;
        let content = '';
        if(this.progress.Total > 0){
          content = `${text['SYNCHRO.LOADING']}<br>${this.progress.Current}/${this.progress.Total}`;
        } else {
          content = `${text['SYNCHRO.LOADING']}`;
        }
        this.showLoading(content);
      
        if (this.progress.Finished) {
          if (this.progress.Error) {
            this.dismissLoading();
            this.insomnia.allowSleepAgain();

            this.showSynchroErrorPopup();
          }
          else {
            this.storageService.getAllDataFromStorage()
            .then(data => {
              this.dismissLoading();
              this.insomnia.allowSleepAgain();

              //afficher les notifs
              this.notificationService.getNotifications()
              .then((notifications:Notification[]) => {
                this.notifications = notifications;
                this.notificationService.showAllNotifications()
                .then(notifications => {
                  this.lastSyncDate = this.startSyncDate;
                  this.setLastLoginOnlineDate();
                  this.synchroService.setSynchroDate(this.startSyncDate).then(() => {
                    this.goToBriefcase();
                  }, () => {
                    this.goToBriefcase();
                  });  
                });
              });   
            });   
          }
        }
      });
    });
  }

  //Get all data from webservices into the Web application
  getAllDataFromWSInWebApp(){
    console.log("GET ALL DATA FROM WS IN WEB APP");
    this.webservice.getAllData().subscribe((progressData: SynchroProgress) => {
      this.translate.get(['SYNCHRO.LOADING']).subscribe(text => {
        this.progress = progressData;
        let content = `${text['SYNCHRO.LOADING']}`;
        
        this.showLoading(content);
      
        if (this.progress.Finished) {
          if (this.progress.Error) {
            this.dismissLoading();
            this.showSynchroErrorPopup();
          } else {
            this.storageService.getAllDataFromStorage().then(data => {
              this.dismissLoading();
              this.goToBriefcase();
            });   
          }       
        }
      });
    });
  }

  synchroProcess(){
    this.showSynchroPopup().then(startSyncBool => {
      if(startSyncBool) {
        this.synchroAllData();
      } else {
        this.goToBriefcase();
      }
    });
  }

  synchroAllData(){
    
    if(this.isIosPlatform){
      this.insomnia.keepAwake();
      this.synchroService.synchroAllData().subscribe((progressData: SynchroProgress) => {
        this.progress = progressData;        
          this.showLoadingProgress();

          if (this.progress.Finished) {
            this.insomnia.allowSleepAgain();

            if (this.progress.Error) {
              this.showSynchroErrorPopup().then(() => {
                this.storageService.getAllDataFromStorage().then(() => {
                  this.goToBriefcase();
                });
              });
            }
            else {
              this.storageService.getAllDataFromStorage().then(data => {
                this.synchroService.setSynchroDate(this.startSyncDate).then(() => {
                  this.goToBriefcase();
                }, () => {
                  this.goToBriefcase();
                });
              });
            }
          }
      });
    } else {
      this.synchroService.synchroAllLists().then((data: any) => {
        this.storageService.getAllDataFromStorage().then(data => {
          this.synchroService.setSynchroDate(this.startSyncDate).then(() => {
            this.goToBriefcase();
          }, () => {
            this.goToBriefcase();
          });
        });
      });
    }
  }

  setUserActions(){
    this.userInfoService.setUserActions();
  }

  //Redict on briefcase or on notification page after click on "skip all" notifications
  goToBriefcase(){
    this.userInfoService.setUserInformation()
    .then(result => {
      this.dismissLoading();
      if(this.notificationService.skipAllStatus){
        this.helperService.setActivePage(NotificationsPage);
        this.navCtrl.setRoot(NotificationsPage);
      } else {
        this.helperService.setActivePage(BriefcasePage);
        this.navCtrl.setRoot(BriefcasePage);
      }
    })
    .catch(err => {
      this.dismissLoading();
      if(this.notificationService.skipAllStatus){
        this.helperService.setActivePage(NotificationsPage);
        this.navCtrl.setRoot(NotificationsPage);
      } else {
        this.helperService.setActivePage(BriefcasePage);
        this.navCtrl.setRoot(BriefcasePage);
      }
    });  
  }

  // Loading or alert controllers

  showLoading(updateContent?: string) {
    this.translate.get(['SYNCHRO.WAIT']).subscribe(text => {
      let content = text['SYNCHRO.WAIT'];
      if(typeof this.loading == 'undefined' || this.loading == null){
        this.loading = this.loadingCtrl.create({
          content: content
        });
      }
      if(updateContent !== null){
        this.loading.setContent(updateContent);
      }
      this.loading.present();
    });
  }

  dismissLoading(){
    if(this.loading){
      this.loading.dismiss();
      this.loading = null;
    }
  }

  showLoadingProgress() {
    this.translate.get(['SYNCHRO.LOADING', 'SYNCHRO.PROGRESS', 'SYNCHRO.ON']).subscribe(text => {
      let content = '';
      if(this.progress.Total > 0){
        content = `${text['SYNCHRO.LOADING']}<br>${text['SYNCHRO.PROGRESS']} : ${this.progress.Current} ${text['SYNCHRO.ON']} ${this.progress.Total}`
      } else {
        content = `${text['SYNCHRO.LOADING']}<br>${text['SYNCHRO.PROGRESS']}`
      }
      
      if(typeof this.loading == 'undefined' || this.loading == null){
        this.loading = this.loadingCtrl.create({
          content: content
        });
      }

      if(content !== ''){
        this.loading.setContent(content);
      }

      this.loading.present();
    });
  }

  showRequireNetworkPopup(){
    //Ensure loading is dismissed
    this.dismissLoading();

    return new Promise((resolve, reject) => {

      this.translate.get(['ERROR.INTERNET_TITLE','ERROR.INTERNET','GENERAL.OK']).subscribe(text => {
        let alert = this.alertCtrl.create({
          title: text["ERROR.INTERNET_TITLE"],
          message: text["ERROR.INTERNET"],
          buttons: [
            {
              text: text['GENERAL.OK'],
              handler: () => {
                resolve();
              }
            }
          ]
        });
        alert.present();
      });

    });
  }

  showSynchroPopup(){
    //Ensure loading is dismissed
    this.dismissLoading();

    return new Promise((resolve, reject) => {

      this.translate.get(['SYNCHRO.NEWCONTENT_TITLE', 'SYNCHRO.NEWCONTENT_TEXT', 'SYNCHRO.LATER', 'GENERAL.YES']).subscribe(text => {
        let alert = this.alertCtrl.create({
          title: text['SYNCHRO.NEWCONTENT_TITLE'],
          message: text['SYNCHRO.NEWCONTENT_TEXT'],
          buttons: [
            {
              text: text['SYNCHRO.LATER'],
              handler: () => {
                resolve(false);
              }
            },
            {
              text: text['GENERAL.YES'],
              handler: () => {
                resolve(true);
              }
            }
          ]
        });
        alert.present();
      });
    });
  }

  showErrorLoginPopup(){
    //Ensure loading is dismissed
    this.dismissLoading();

    return new Promise((resolve, reject) => {
      this.translate.get(['ERROR.ACCESS_DENIED_TITLE','ERROR.ACCESS_DENIED_TEXT','GENERAL.OK']).subscribe(text => {
        let alert = this.alertCtrl.create({
          title: text['ERROR.ACCESS_DENIED_TITLE'],
          message: text['ERROR.ACCESS_DENIED_TEXT'],
          buttons: [
            {
              text: text['GENERAL.OK'],
              handler: () => {
                resolve(true);
              }
            }
          ]
        });
        alert.present();
      });
    });
  }

  showSynchroErrorPopup(){
    //Ensure loading is dismissed
    this.dismissLoading();

    return new Promise((resolve, reject) => {
      this.translate.get(['ERROR.SYNCHRO_FAILED_TITLE','ERROR.SYNCHRO_FAILED_TEXT','GENERAL.OK']).subscribe(text => {
        let alert = this.alertCtrl.create({
          title: text['ERROR.SYNCHRO_FAILED_TITLE'],
          message: text['ERROR.SYNCHRO_FAILED_TEXT'],
          buttons: [
            {
              text: text['GENERAL.OK'],
              handler: () => {
                resolve(true);
              }
            }
          ]
        });
        alert.present();
      });
    });
  }

  /* LAST LOGIN DATE FOR NOTIFICATIONS */
  setLastLoginOnlineDate(){
    this.storageService.setDataStorage("lastLoginOnlineDate", this.helperService.getFullDate(2));
  }

  getLastLoginOnlineDate(){
    return this.storageService.getDataStorage("lastLoginOnlineDate");
  }
}



// WEBPACK FOOTER //
// ./src/pages/login/login.ts